/**
 * @file clientService.ts
 * @description Service for handling client-related API calls.
 */

import config from "../config/config";
import { auth } from "../config/firebaseConfig";
import log from "../config/logging";
import { Client } from "../types";

/**
 * Get all clients for the current user.
 * @returns Array of clients
 */
export const getClients = async (): Promise<Client[]> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(`${config.backendUrl}/api/clients`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch clients: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.clients || [];
  } catch (error) {
    log.error("Error fetching clients", { error });
    throw error;
  }
};

/**
 * Create a new client.
 * @param name - Client name/description
 * @returns The created client ID
 */
export const createClient = async (name: string): Promise<string> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(`${config.backendUrl}/api/clients`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to create client: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.clientId;
  } catch (error) {
    log.error("Error creating client", { error });
    throw error;
  }
};

/**
 * Update an existing client.
 * @param clientId - ID of the client to update
 * @param name - New client name/description
 */
export const updateClient = async (
  clientId: string,
  name: string
): Promise<void> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(`${config.backendUrl}/api/clients/${clientId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to update client: ${response.statusText}`
      );
    }
  } catch (error) {
    log.error("Error updating client", { error });
    throw error;
  }
};

/**
 * Delete a client.
 * @param clientId - ID of the client to delete
 */
export const deleteClient = async (clientId: string): Promise<void> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(`${config.backendUrl}/api/clients/${clientId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to delete client: ${response.statusText}`
      );
    }
  } catch (error) {
    log.error("Error deleting client", { error });
    throw error;
  }
};
